import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DcButton,
  DcDropDown,
  DcInput,
  DcOverlaySlider,
  PixelFactoryContext,
  PixelTable,
} from 'pixel-base'
import {
  cancelFinanceInvoice,
  confirmFinanceInvoice,
  createFinanceInvoice,
  deleteFinanceInvoice,
  getChartOfAccounts,
  getFinanceInvoices,
  getJournals,
  getPartners,
  postFinanceInvoice,
  reverseFinanceInvoice,
  updateFinanceInvoice,
} from 'utils/api-routes-accounts'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatusBadge from '../../components/FinanceStatusBadge'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { Account, Invoice, Journal, Partner } from '../../types/finance'
import formatFinanceDate from '../../utils/formatFinanceDate'

const today = new Date().toISOString().split('T')[0]

const defaultForm = {
  id: null,
  version_no: 1,
  partner_id: '',
  journal_id: '',
  counterparty_account_id: '',
  tax_account_id: '',
  currency_code: 'PKR',
  document_date: today,
  posting_date: today,
  due_date: '',
  reference: '',
  notes: '',
  line_account_head_id: '',
  line_description: '',
  quantity: 1,
  unit_price: 0,
  tax_amount: 0,
  cost_center_id: '',
}

const isEditableState = (state?: string) =>
  ['draft', 'confirmed'].includes((state || 'draft').toLowerCase())

const canReverseState = (state?: string) =>
  ['posted', 'partially_paid', 'paid'].includes((state || '').toLowerCase())

const Invoices = () => {
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const navigate = useNavigate()
  const invoices = useFinanceObservable<Invoice[]>(() => getFinanceInvoices())
  const partners = useFinanceObservable<Partner[]>(() => getPartners())
  const journals = useFinanceObservable<Journal[]>(() => getJournals())
  const accounts = useFinanceObservable<Account[]>(() => getChartOfAccounts())
  const [isOpen, setIsOpen] = React.useState(false)
  const [form, setForm] = React.useState<any>(defaultForm)

  const reset = () => {
    setForm(defaultForm)
    setIsOpen(false)
  }

  const hydrateForm = (row: Invoice) => {
    const line = row.lines?.[0] || {}
    setForm({
      id: row.id,
      version_no: row.version_no || 1,
      partner_id: row.partner_id || '',
      journal_id: row.journal_id || '',
      counterparty_account_id: row.counterparty_account_id || '',
      tax_account_id: row.tax_account_id || '',
      currency_code: row.currency_code || 'PKR',
      document_date: formatFinanceDate(row.document_date) || today,
      posting_date: formatFinanceDate(row.posting_date) || today,
      due_date:
        formatFinanceDate(row.due_date) === '--'
          ? ''
          : formatFinanceDate(row.due_date),
      reference: row.reference || '',
      notes: row.notes || '',
      line_account_head_id: line.account_head_id || '',
      line_description: line.description || '',
      quantity: line.quantity || 1,
      unit_price: line.unit_price || 0,
      tax_amount: line.tax_amount || 0,
      cost_center_id: line.cost_center_id || '',
    })
    setIsOpen(true)
  }

  const save = () => {
    const payload = {
      partner_id: Number(form.partner_id),
      journal_id: Number(form.journal_id),
      counterparty_account_id: Number(form.counterparty_account_id),
      tax_account_id: form.tax_account_id
        ? Number(form.tax_account_id)
        : undefined,
      currency_code: form.currency_code || 'PKR',
      document_date: form.document_date,
      posting_date: form.posting_date,
      due_date: form.due_date || undefined,
      reference: form.reference || undefined,
      notes: form.notes || undefined,
      version_no: Number(form.version_no || 1),
      lines: [
        {
          account_head_id: Number(form.line_account_head_id),
          description: form.line_description || undefined,
          quantity: Number(form.quantity || 1),
          unit_price: Number(form.unit_price || 0),
          tax_amount: Number(form.tax_amount || 0),
          cost_center_id: form.cost_center_id
            ? Number(form.cost_center_id)
            : undefined,
        },
      ],
    }

    const request = form.id
      ? updateFinanceInvoice(form.id, payload)
      : createFinanceInvoice(payload)
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(
          form.id ? 'Invoice updated' : 'Invoice created',
        )
        reset()
        invoices.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Unable to save invoice'),
    })
  }

  const runAction = (request, successMessage: string) => {
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(successMessage)
        invoices.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Invoice action failed'),
    })
  }

  return (
    <>
      <FinancePageShell
        title="Invoices"
        isLoading={invoices.loading}
        primaryAction={{ label: 'New Invoice', onClick: () => setIsOpen(true) }}
      >
        <PixelTable
          columns={[
            {
              name: 'Number',
              selector: (row) => row.number || `INV-${row.id}`,
            },
            {
              name: 'Partner',
              selector: (row) => row.partner?.name || row.partner_id || '--',
            },
            {
              name: 'Document Date',
              selector: (row) => formatFinanceDate(row.document_date),
            },
            {
              name: 'Due Date',
              selector: (row) => formatFinanceDate(row.due_date),
            },
            { name: 'Total', selector: (row) => row.amount_total ?? '--' },
            {
              name: 'Residual',
              selector: (row) => row.amount_residual ?? '--',
            },
            {
              name: 'Status',
              selector: (row) => <FinanceStatusBadge status={row.state} />,
            },
            {
              name: 'Actions',
              selector: (row) => {
                const state = (row.state || 'draft').toLowerCase()
                const canEdit = isEditableState(state)
                const canConfirm = state === 'draft'
                const canPost = state === 'confirmed'
                const canReverse = canReverseState(state)
                const canCancel = ['draft', 'confirmed'].includes(state)
                const canDelete = ['draft', 'confirmed'].includes(state)

                return (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <DcButton
                      size="sm"
                      variant="outline-primary"
                      onClick={() => navigate(`/finance/invoices/${row.id}`)}
                    >
                      View
                    </DcButton>
                    {canEdit && (
                      <DcButton
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => hydrateForm(row)}
                      >
                        Edit
                      </DcButton>
                    )}
                    {canConfirm && (
                      <DcButton
                        size="sm"
                        variant="outline-primary"
                        onClick={() =>
                          runAction(
                            confirmFinanceInvoice(row.id),
                            'Invoice confirmed',
                          )
                        }
                      >
                        Confirm
                      </DcButton>
                    )}
                    {canPost && (
                      <DcButton
                        size="sm"
                        variant="outline-success"
                        onClick={() =>
                          runAction(
                            postFinanceInvoice(row.id, {
                              idempotency_key: `invoice-post-${row.id}`,
                            }),
                            'Invoice posted',
                          )
                        }
                      >
                        Post
                      </DcButton>
                    )}
                    {canReverse && (
                      <DcButton
                        size="sm"
                        variant="outline-warning"
                        onClick={() =>
                          runAction(
                            reverseFinanceInvoice(row.id, {
                              idempotency_key: `invoice-reverse-${row.id}`,
                            }),
                            'Invoice reversed',
                          )
                        }
                      >
                        Reverse
                      </DcButton>
                    )}
                    {canCancel && (
                      <DcButton
                        size="sm"
                        variant="outline-danger"
                        onClick={() =>
                          runAction(
                            cancelFinanceInvoice(row.id),
                            'Invoice cancelled',
                          )
                        }
                      >
                        Cancel
                      </DcButton>
                    )}
                    {canDelete && (
                      <DcButton
                        size="sm"
                        variant="outline-danger"
                        onClick={() =>
                          runAction(
                            deleteFinanceInvoice(row.id),
                            'Invoice deleted',
                          )
                        }
                      >
                        Delete
                      </DcButton>
                    )}
                  </div>
                )
              },
            },
          ]}
          data={invoices.data || []}
          searchable
          pagination
        />
      </FinancePageShell>
      <DcOverlaySlider
        isOpen={isOpen}
        onClose={reset}
        title={form.id ? 'Edit Invoice' : 'Create Invoice'}
        width="38%"
      >
        <div style={{ padding: 20, display: 'grid', gap: 16 }}>
          <DcDropDown
            value={form.partner_id}
            options={(partners.data || []).map((partner) => ({
              value: partner.id,
              label: `${partner.code} - ${partner.name}`,
            }))}
            onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
            label="Partner"
          />
          <DcDropDown
            value={form.journal_id}
            options={(journals.data || []).map((journal) => ({
              value: journal.id,
              label: journal.name,
            }))}
            onChange={(e) => setForm({ ...form, journal_id: e.target.value })}
            label="Journal"
          />
          <DcDropDown
            value={form.counterparty_account_id}
            options={(accounts.data || []).map((account) => ({
              value: account.id,
              label: `${account.code} - ${account.name}`,
            }))}
            onChange={(e) =>
              setForm({ ...form, counterparty_account_id: e.target.value })
            }
            label="Receivable Account"
          />
          <DcDropDown
            value={form.line_account_head_id}
            options={(accounts.data || []).map((account) => ({
              value: account.id,
              label: `${account.code} - ${account.name}`,
            }))}
            onChange={(e) =>
              setForm({ ...form, line_account_head_id: e.target.value })
            }
            label="Revenue Account"
          />
          <DcInput
            label="Reference"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
          />
          <DcInput
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
          <DcInput
            label="Line Description"
            value={form.line_description}
            onChange={(e) =>
              setForm({ ...form, line_description: e.target.value })
            }
          />
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
          >
            <DcInput
              type="date"
              label="Document Date"
              value={form.document_date}
              onChange={(e) =>
                setForm({ ...form, document_date: e.target.value })
              }
            />
            <DcInput
              type="date"
              label="Posting Date"
              value={form.posting_date}
              onChange={(e) =>
                setForm({ ...form, posting_date: e.target.value })
              }
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 12,
            }}
          >
            <DcInput
              type="number"
              label="Quantity"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            />
            <DcInput
              type="number"
              label="Unit Price"
              value={form.unit_price}
              onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
            />
            <DcInput
              type="number"
              label="Tax Amount"
              value={form.tax_amount}
              onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
            />
          </div>
          <DcInput
            type="date"
            label="Due Date"
            value={form.due_date}
            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
          />
          <DcButton onClick={save}>Save Invoice</DcButton>
        </div>
      </DcOverlaySlider>
    </>
  )
}

export default Invoices
